import { GLManager } from '@play-co/timestep-core/renderer';
import {
  CustomFilter,
  ImageView,
  View,
  animate,
} from '@play-co/timestep-core/ui';
import { HueShift, getHueShift } from '../shaders/HueShift';
import { CardRarity } from 'src/replicant/data/cards/Cards.schema';
import { animDuration } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  CardId,
  cardsCatalog,
  collectionColors,
} from 'src/replicant/cards/cards.ruleset';
import i18n from 'src/lib/i18n/i18n';
import { CardsBadge } from './CardsBadge';
import ButtonScaleView, {
  ButtonScaleViewOpts,
} from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import LazyImageView from 'src/lib/ui/components/LazyImageView';
import { cardsGetCardIcon } from '../cards.assets';

export type CardState = {
  id: CardId;
  new?: boolean;
  count: number;
};

export class Card extends ButtonScaleView {
  private content: View;
  private front: LazyImageView;
  private back: ImageView;
  private slot: ImageView;
  private newBadge: CardsBadge;
  private setColor: (color: string) => void;
  private setName: (name: string) => void;
  private setCount: (count: number) => void;
  private setRarity: (rarity: CardRarity) => void;
  public currentState?: CardState;

  constructor(opts: Omit<ButtonScaleViewOpts, 'tracking'>) {
    super({
      width: 184,
      height: 238,
      ...opts,
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.DETAILS,
      },
    });

    // Content is a wrapper for all the card elements.
    this.content = new View({
      superview: this,
      width: 184,
      height: 238,
    });

    //  Card slot.
    this.slot = new ImageView({
      superview: this.content,
      width: 184,
      height: 238,
      image: 'assets/ui/cards/common/card_slot.png',
      centerAnchor: true,
    });
    const slotName = new LangBitmapFontTextView({
      superview: this.slot,
      width: 150,
      height: 36,
      size: 22,
      color: '#ABA995',
      align: 'center',
      verticalAlign: 'center',
      localeText: () => 'Card Name',
      centerOnOrigin: true,
      font: 'Body',
      x: this.style.width / 2,
      y: this.style.height / 2 + 66,
    });

    // Back of the card.
    this.back = new ImageView({
      superview: this.content,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/cards/common/card_back.png',
      centerAnchor: true,
      visible: false,
    });

    // Card artwork is assigned by setState so empty slots never reveal it.
    this.front = new LazyImageView({
      superview: this.content,
      width: this.style.width,
      height: this.style.height,
      centerAnchor: true,
      visible: false,
    });

    const hueShiftShader = GLManager.isSupported
      ? GLManager.createShader(HueShift)
      : null;
    const titleFrame = new ImageView({
      superview: this.front,
      width: 182,
      height: 46,
      image: 'assets/ui/cards/common/card_title.png',
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.style.height - 20,
      filter: hueShiftShader ? new CustomFilter(hueShiftShader) : undefined,
    });

    const frontText = new LangBitmapFontTextView({
      superview: titleFrame,
      width: titleFrame.style.width - 20,
      height: titleFrame.style.height,
      size: 22,
      color: '#ffffff',
      align: 'center',
      verticalAlign: 'center',
      localeText: () => 'Card Name',
      font: 'Body',
      x: 9,
      y: -5,
    });

    const frame = new ImageView({
      superview: this.front,
      width: 220,
      height: 290,
      image: 'assets/ui/cards/common/card_frame.png',
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.style.height / 2,
      filter: hueShiftShader ? new CustomFilter(hueShiftShader) : undefined,
    });

    const frameSpecial = new ImageView({
      superview: this.front,
      width: 220,
      height: 290,
      image: 'assets/ui/cards/common/card_frame_special.png',
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.style.height / 2,
      visible: false,
    });

    const countBg = new ImageView({
      superview: this.front,
      image: 'assets/ui/cards/common/card_count.png',
      width: 55,
      height: 45,
      x: 20,
      y: 50,
      centerOnOrigin: true,
    });

    const counterText = new LangBitmapFontTextView({
      superview: countBg,
      width: countBg.style.width,
      height: 27,
      x: countBg.style.width / 2 - 4,
      y: countBg.style.height / 2 - 6,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      color: '#FFFFFF',
      localeText: () => '1',
    });

    const stars = new ImageView({
      superview: this.front,
      image: 'assets/ui/cards/common/icon_star1.png',
      width: 187,
      height: 69,
      x: this.style.width / 2,
      y: 0,
      centerOnOrigin: true,
    });

    this.newBadge = new CardsBadge({
      superview: this.front,
      x: 165,
      y: 40,
      visible: false,
      scale: 0.6,
      text: i18n('cards.new'),
    });

    // Setters.
    this.setColor = (color: string) => {
      if (!hueShiftShader) return;
      hueShiftShader.shift = getHueShift('#E53737', color);
    };

    this.setName = (name: string) => {
      slotName.localeText = () => name;
      frontText.localeText = () => name;
    };

    this.setCount = (count: number) => {
      if (count <= 1) {
        countBg.hide();
        return;
      }
      countBg.show();
      counterText.localeText = () => (count >= 10 ? '9+' : count.toString());
    };

    this.setRarity = (rarity: CardRarity) => {
      stars.updateOpts({
        image: `assets/ui/cards/common/icon_star${rarity}.png`,
      });
      frameSpecial.updateOpts({
        visible: rarity >= 5,
      });
    };
  }

  public setState(state?: CardState) {
    this.currentState = state;
    if (!state) {
      this.content.hide();
      return;
    }

    this.content.show();
    this.setName(i18n(`cards.cards.${state.id}.name`));

    //  Only show card slot if not owned.
    if (state.count <= 0) {
      this.slot.show();
      this.back.hide();
      this.front.hide();
      return;
    }

    this.slot.hide();
    const cardData = cardsCatalog[state.id];
    this.setColor(collectionColors[cardData.collection]);
    this.setCount(state.count);
    this.setRarity(cardData.rarity);
    this.newBadge.updateOpts({ visible: !!state.new });
    this.front.spinnerColor = collectionColors[cardData.collection];
    this.front.updateImage(cardsGetCardIcon(state.id));
    this.flip(false, 0);
  }

  public async flip(back: boolean, duration = animDuration) {
    const visible = back ? this.front : this.back;
    const hidden = back ? this.back : this.front;

    visible.updateOpts({ visible: true, scaleX: 1 });
    hidden.updateOpts({ visible: true, scaleX: 0 });

    await animate(visible)
      .clear()
      .now({ scaleX: 1 })
      .then({ scaleX: 0 }, duration / 2)
      .toPromise();
    await animate(hidden)
      .clear()
      .now({ scaleX: 0 })
      .then({ scaleX: 1 }, duration / 2)
      .toPromise();

    visible.updateOpts({ visible: false, scaleX: 0 });
    hidden.updateOpts({ visible: true, scaleX: 1 });
  }
}
